export const FORM_TYPE_LABELS: Record<string, string> = {
  '00': 'Application for issuance of new IT equipment',
  '11': 'Application for creation of e-office ID',
  '12': 'Application for creation of VPP/MPR portal ID',
  '13': 'Application for creation of VPN ID',
  '14': 'Application for Third-Party Email Access Request',
};

export const IT_EQUIPMENT_FALLBACK = [
  { code: '1', label: 'TFT/Monitor' },
  { code: '2', label: 'LED TV' },
  { code: '3', label: 'Workstation and Monitor' },
  { code: '4', label: 'CPU' },
  { code: '5', label: 'Portable hard disk' },
  { code: '6', label: 'External SSD' },
  { code: '7', label: 'Printer' },
  { code: '8', label: 'Scanner' },
  { code: '9', label: 'UPS' },
  { code: '10', label: 'Plotter' },
  { code: '11', label: 'Webcam' },
  { code: '12', label: 'Headphone' },
  { code: '13', label: 'Internal SSD' },
  { code: '14', label: 'Laptop' },
  { code: '15', label: 'others' },
];

export const YES_NO_FALLBACK = [
  { code: '1', label: 'Yes' },
  { code: '2', label: 'No' },
];

export const SUPERVISOR_FALLBACK = [
  { code: '1', label: 'Yes' },
  { code: '2', label: 'No' },
  { code: '3', label: 'Not Applicable' },
];

export const VPN_COMPLIANCE = [
  'Will ensure that Operating system and antivirus will be updated regularly.',
  'Will not share/forward any or part of the VPN ID details to anyone.',
  'Will only use client provided by IT department for VPN connection.',
  'Will intimate IT Department when VPN connection is not required/project timeline is over.',
  'IT Department may disable the VPN account if inactive for 90 days (Ref: IT security Policy 2019, Clause 3.2.5).',
];

export const PRINTER_NOTE =
  'Printer will only be issued if no printer is available for sharing at nearby location.';

const SUBMITTED_STATUS = new Set(['N', 'T', 'P', 'A', 'R', 'D', 'X', 'B', 'C', 'K', 'S']);

export interface MasterOption {
  code: string;
  label: string;
}

export function readSessionUser(): any {
  try {
    const raw = localStorage.getItem('user');
    if (!raw || raw === 'undefined' || raw === 'null') return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function expandUsers(root: any): any[] {
  const list: any[] = [];
  const queue = [root];
  const seen = new Set<any>();
  while (queue.length && list.length < 16) {
    const node = queue.shift();
    if (!node || typeof node !== 'object' || seen.has(node)) continue;
    seen.add(node);
    list.push(node);
    if (node.user && typeof node.user === 'object') queue.push(node.user);
    if (node.employee && typeof node.employee === 'object') queue.push(node.employee);
    if (node.data && typeof node.data === 'object') queue.push(node.data);
  }
  return list;
}

function officeLabel(office: any): string {
  if (office == null || office === '') return '';
  if (typeof office === 'string' || typeof office === 'number') {
    const text = String(office).trim();
    return text && text !== 'null' && text !== 'undefined' ? text : '';
  }
  if (typeof office !== 'object') return '';
  const values = [
    office.name,
    office.office_name,
    office.OfficeName,
    office.officeName,
    office.location,
    office.location_name,
    office.station_name,
    office.officeLocation?.name,
    office.office_location?.name,
    office.office_location_details?.name,
    office.OfficeLocation?.name,
    office.officeLocation?.office_name,
    office.office_location_details?.office_name,
  ];
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text && text !== 'null' && text !== 'undefined') return text;
  }
  return '';
}

function officeRows(user: any): any[] {
  const groups = [
    user?.offices,
    user?.assigned_offices,
    user?.employee?.offices,
    user?.employee?.assigned_offices,
    user?.details?.offices,
    user?.details?.assigned_offices,
  ];
  const rows: any[] = [];
  groups.forEach(group => {
    if (Array.isArray(group)) rows.push(...group);
    else if (Array.isArray(group?.results)) rows.push(...group.results);
    else if (group && typeof group === 'object') rows.push(group);
  });
  return rows;
}

function textOffice(user: any): string {
  const details = user?.details || {};
  const wings = details?.sap_department_designation_wings || user?.sap_department_designation_wings || {};
  const values = [
    details.office_location,
    details.officeLocation,
    details.place_of_posting,
    details.placeOfPosting,
    details.station_name,
    details.office_name,
    details.work_location,
    wings.office_name,
    wings.location_name,
    wings.office,
    user?.officeLocation,
    user?.OfficeLocation,
  ];
  for (const value of values) {
    if (value && typeof value === 'object') {
      const nested = officeLabel(value);
      if (nested) return nested;
      continue;
    }
    const text = String(value ?? '').trim();
    if (text && text !== 'null' && text !== 'undefined') return text;
  }
  return '';
}

/** Same source as the app: login user.offices[0].name. Profile fills the gap when login offices were cleared. */
function resolveOfficeLocation(sessionUser: any, profileState: any): string {
  const users = [
    ...expandUsers(sessionUser),
    ...expandUsers(profileState?.data),
    ...expandUsers(profileState?.controllingOfficerData),
  ];
  for (const user of users) {
    for (const row of officeRows(user)) {
      const label = officeLabel(row);
      if (label) return label;
    }
  }
  for (const user of users) {
    const label = textOffice(user);
    if (label) return label;
  }
  return '';
}

function firstUser(nodes: any[]): any {
  return nodes.find(node => node?.details || node?.empId || node?.offices) || null;
}

/** Login user is the same source the mobile app uses for autofill. Profile API fills gaps. */
export function resolveEmployee(profileState: any, sessionUser: any) {
  const apiUser =
    firstUser(expandUsers(profileState?.data)) ||
    firstUser(expandUsers(profileState?.controllingOfficerData));
  const session = firstUser(expandUsers(sessionUser)) || sessionUser;

  const details = {
    ...(session?.details || {}),
    ...(apiUser?.details || {}),
  };
  const wings = details?.sap_department_designation_wings || {};
  const office = resolveOfficeLocation(sessionUser, profileState);

  const phoneRaw =
    details?.phone ||
    details?.mobileNo ||
    details?.mobile_no ||
    details?.mobile ||
    details?.contactNumber ||
    sessionUser?.phone ||
    '';

  return {
    empId: String(apiUser?.empId || sessionUser?.empId || details?.empId || details?.employee_id || ''),
    employeeName:
      [details?.firstname, details?.lastname || details?.lsastname].filter(Boolean).join(' ').trim() ||
      details?.fullName ||
      sessionUser?.name ||
      '',
    designation: wings?.designation_name || details?.designation || '',
    department: wings?.department_name || details?.department || '',
    wing: wings?.wing || wings?.wing_name || details?.wing || '',
    mobileNo: String(phoneRaw || '').replace(/\D/g, '').slice(0, 10),
    email: details?.email || details?.official_email || sessionUser?.email || '',
    officeLocation: office || '',
    dateOfJoining: details?.date_of_joining || details?.dateOfJoining || details?.DateOfJoining || '',
    dateOfBirth: details?.birthdate || details?.date_of_birth || details?.dob || '',
    appointmentType: 'Direct Recruit',
  };
}

export function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function dmyFromParts(day: string, month: string, year: string) {
  if (!day || !month || !year || month === '00' || day === '00') return '';
  if (month.length !== 2 || day.length !== 2 || year.length !== 4) return '';
  return `${day}-${month}-${year}`;
}

function dmyFromDate(date: Date) {
  if (isNaN(date.getTime())) return '';
  return dmyFromParts(pad2(date.getDate()), pad2(date.getMonth() + 1), String(date.getFullYear()));
}

/** Display / SAP date as DD-MM-YYYY. Never returns a broken "dd--yyyy". */
export function formatDisplayDate(value: any): string {
  if (value == null || value === '') return '';
  const str = String(value).trim();
  if (!str || str === 'null' || str === 'undefined') return '';

  const odata = str.match(/\/Date\((-?\d+)\)\//);
  if (odata) return dmyFromDate(new Date(parseInt(odata[1], 10)));

  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return dmyFromParts(iso[3], iso[2], iso[1]);

  const dmy = str.match(/^(\d{2})[-/.](\d{2})[-/.](\d{4})$/);
  if (dmy) return dmyFromParts(dmy[1], dmy[2], dmy[3]);

  if (/^\d{8}$/.test(str)) {
    return dmyFromParts(str.slice(6, 8), str.slice(4, 6), str.slice(0, 4));
  }

  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) return dmyFromDate(parsed);
  return '';
}

/** HTML date input value (YYYY-MM-DD) from any SAP/API date. */
export function toInputDate(value: any): string {
  const dmy = formatDisplayDate(value);
  const m = dmy.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return '';
  return `${m[3]}-${m[2]}-${m[1]}`;
}

/** Payload date, same as the app: DD-MM-YYYY. */
export function toSubmitDate(value: any): string {
  return formatDisplayDate(value);
}

export function normalizeFormType(code: any): string {
  const c = String(code ?? '').trim();
  if (c === '0' || c === '00') return '00';
  return c;
}

export function formTypeLabel(code: any, masterList?: any[], apiText?: string): string {
  const text = String(apiText || '').trim();
  if (text && !/^\d+$/.test(text)) return text;
  const normalized = normalizeFormType(code);
  if (Array.isArray(masterList)) {
    const found = masterList.find((m: any) => normalizeFormType(m?.Value ?? m?.value ?? m?.code) === normalized);
    const label = String(found?.Ddtext || found?.label || found?.Text || '').trim();
    if (label) return label;
  }
  return FORM_TYPE_LABELS[normalized] || (normalized ? normalized : 'IT Request');
}

export function masterOptions(master: any, key: string, fallback: MasterOption[] = []): MasterOption[] {
  const list = master?.[key];
  if (!Array.isArray(list) || list.length === 0) return fallback;
  const mapped = list
    .map((item: any) => ({
      code: String(item?.Value ?? item?.value ?? item?.code ?? item?.Code ?? '').trim(),
      label: String(item?.Ddtext ?? item?.ddtext ?? item?.label ?? item?.Text ?? item?.text ?? '').trim(),
    }))
    .filter((item: MasterOption) => item.code !== '' && item.label !== '');
  return mapped.length ? mapped : fallback;
}

export function optionLabel(options: MasterOption[], code: any, fallback = '-'): string {
  const c = String(code ?? '').trim();
  if (!c) return fallback;
  return options.find(o => o.code === c)?.label || fallback;
}

function isMasterNode(node: any): boolean {
  return Boolean(
    node &&
    typeof node === 'object' &&
    !Array.isArray(node) &&
    (node.Form_Type || node.Item_List_Type || node.Supervisor_Type || node.Services_Type)
  );
}

function isRequestNode(node: any): boolean {
  return Boolean(
    node &&
    typeof node === 'object' &&
    !Array.isArray(node) &&
    (node.ReferenceNumber || node.referenceNo || node.EmpID || node.SelectRequestFromType)
  );
}

/** Walk the double/triple `{ data: { success, data } }` envelope used by e-requisition. */
function walk(node: any, depth: number, visit: (node: any) => any): any {
  if (node == null || depth > 6) return null;
  const hit = visit(node);
  if (hit != null) return hit;
  if (typeof node !== 'object') return null;
  const next = node.data ?? node.details ?? node.result ?? node.payload ?? node.response;
  if (next == null || next === node) return null;
  return walk(next, depth + 1, visit);
}

export function unwrapMaster(payload: any): any {
  return walk(payload, 0, (node) => (isMasterNode(node) ? node : null)) || {};
}

export function unwrapList(payload: any): any[] {
  const found = walk(payload, 0, (node) => {
    if (Array.isArray(node)) return node;
    if (!node || typeof node !== 'object') return null;
    const direct = [node.items, node.results, node.rows, node.requests, node.records, node.list];
    for (const candidate of direct) {
      if (Array.isArray(candidate)) return candidate;
    }
    if (Array.isArray(node.data)) return node.data;
    if (Array.isArray(node?.data?.items)) return node.data.items;
    if (Array.isArray(node?.data?.results)) return node.data.results;
    return null;
  });
  return Array.isArray(found) ? found : [];
}

export function unwrapRecord(payload: any): any {
  return walk(payload, 0, (node) => (isRequestNode(node) ? node : null));
}

export function isApiSuccess(payload: any): boolean {
  if (payload == null) return false;
  if (typeof payload === 'string') return false;
  if (payload.success === false || payload.status === false) return false;
  if (payload?.data?.success === false) return false;
  const status = payload.status ?? payload?.data?.status;
  if (typeof status === 'number' && status >= 400) return false;
  return true;
}

export function apiMessage(payload: any, fallback: string): string {
  if (typeof payload === 'string' && payload.trim()) return payload;
  return (
    payload?.message ||
    payload?.data?.message ||
    payload?.error ||
    fallback
  );
}

const DRAFT_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Value find-one can look up. Draft rows store a UUID in ReferenceNumber and a
 * numeric id (2301). Sending that id as ReferenceNumber returns 404.
 */
export function lookupReference(record: any): string {
  if (record == null || record === '') return '';
  if (typeof record === 'string' || typeof record === 'number') {
    return lookupReference({ ReferenceNumber: record });
  }
  const localId = String(record.id ?? '').trim();
  const candidates = [
    record.ReferenceNumber,
    record.referenceNo,
    record.reference_number,
    record.referenceNumber,
  ];
  for (const raw of candidates) {
    const value = String(raw ?? '').trim();
    if (!value || value === 'null' || value === 'undefined') continue;
    if (DRAFT_UUID.test(value)) return value;
    if (/^\d{10,13}$/.test(value)) return value;
    if (/^\d{1,13}$/.test(value) && localId && value !== localId) return value;
  }
  return '';
}

export function isDraftRecord(item: any): boolean {
  if (!item) return false;
  const status = String(item.RequestStatus || item.requestStatus || '').trim().toUpperCase();
  if (SUBMITTED_STATUS.has(status)) return false;
  const flag = item.is_draft;
  if (flag === true || flag === 1 || flag === '1' || flag === 'true') return true;
  const text = String(item.Status || item.status || '').trim().toLowerCase();
  return text === 'draft' || text === 'saved in draft' || text === 'saved_in_draft';
}

export function statusCode(item: any): string {
  if (isDraftRecord(item)) return 'DRAFT';
  const raw = String(item?.RequestStatus || item?.requestStatus || item?.Status || item?.status || '').trim();
  return raw.toUpperCase();
}

export function applicantWing(req: any): string {
  return (
    req?.employeeDetails?.wing ||
    req?.employeeDetails?.sap_department_designation_wings?.wing ||
    req?.employeeDetails?.wing_name ||
    req?.Wing ||
    req?.wing ||
    req?.WingName ||
    req?.wing_name ||
    req?.sap_department_designation_wings?.wing ||
    ''
  );
}

export function applicantJoining(req: any): string {
  return formatDisplayDate(
    req?.DateOfJoining ||
      req?.dateOfJoining ||
      req?.joining_date ||
      req?.employeeDetails?.date_of_joining ||
      req?.employeeDetails?.DateOfJoining ||
      ''
  );
}

/** Mobile display: AlreadyIssueITAsset. Mobile approval: AlreadyIssuedAssets. */
export function alreadyIssuedLabel(req: any, forApprover: boolean): string {
  const code = forApprover ? req?.AlreadyIssuedAssets : req?.AlreadyIssueITAsset;
  return String(code ?? '').trim() === '1' ? 'Yes' : 'No';
}

export function supervisorLabel(code: any): string {
  const c = String(code ?? '').trim();
  if (c === '1') return 'Yes';
  if (c === '2') return 'No';
  return 'Not Applicable';
}

export function firewallLabel(code: any, masterText?: string): string {
  if (masterText && String(masterText).trim()) return String(masterText).trim();
  return String(code ?? '').trim() === '111' ? 'ON' : 'OFF';
}

export function pickApproverId(rem: any, requesterEmpId?: string): string {
  const candidates = [rem?.ApproverID, rem?.ApproverEmpId, rem?.approver_id, rem?.ApproverPernr, rem?.ApproverUserID]
    .map(v => String(v ?? '').trim())
    .filter(v => v && v !== '00000000');
  const requester = String(requesterEmpId || '').replace(/^0+/, '');
  const other = candidates.find(c => c.replace(/^0+/, '') !== requester);
  return other || candidates[0] || '';
}

export function lineItemsOf(req: any): any[] {
  const sources = [req?.ToList, req?.List_Item, req?.items];
  for (const source of sources) {
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.results)) return source.results;
  }
  return [];
}

export function equipmentName(code: any, masterList?: MasterOption[]): string {
  const c = String(code ?? '').trim();
  const fromMaster = masterList?.find(o => o.code === c || o.code === c.replace(/^0+/, '') || o.code.padStart(2, '0') === c.padStart(2, '0'));
  if (fromMaster?.label) return fromMaster.label;
  const fallback = IT_EQUIPMENT_FALLBACK.find(o => o.code === c || o.code === c.replace(/^0+/, ''));
  return fallback?.label || c || '-';
}
